// tslint:disable: no-input-rename no-output-rename

import { Input, TemplateRef, Directive, HostListener, EventEmitter, Output, OnDestroy } from '@angular/core';
import { SheetService, SheetConfig } from './sheet.service';
import { Subscription } from 'rxjs';

const PRIMARY_ACTION_TAG = 'primaryAction';
const SECONDARY_ACTION_TAG = 'secondaryAction';

/**
 * Sheets are overlays that prevent users from interacting with the rest of the application until a specific action is taken. They can be disruptive because they require users to take an action before they can continue interacting with the rest of the application.
 * It should be used thoughtfully and sparingly.
 */
@Directive({
  selector: '[sukaSheet]'
})
export class SheetDirective {
  constructor(protected sheetService: SheetService) { }

  /**
   * Set to `true` to show a close button on the top right corner. Defaults to `false`.
   */
  @Input('sheetCloseButton') closeButton = false;
  /**
   * Sets the sheet title.
   */
  @Input('sheetTitle') title: string | TemplateRef<any>;
  /**
   * Sets the body of the sheet.
   */
  @Input('sheetBody') body: string | TemplateRef<any>;
  /**
   * Sets to `false` to stop the sheet from closing when the user clicks on the backdrop. Defaults to `true`.
   */
  @Input('sheetCloseOnBackdropClick') closeOnBackdropClick = true;

  /**
   * Callback when the sheet closes.
   */
  @Output('sheetClose') close = new EventEmitter();

  /**
   * Sets the sheet secondary action button label.
   */
  @Input('sheetSecondaryActionLabel') secondaryActionLabel: string;
  /**
   * Callback when the secondary action is triggered.
   */
  @Output('sheetSecondaryAction') secondaryAction = new EventEmitter();

  /**
   * Sets the sheet primary action button label.
   */
  @Input('sheetPrimaryActionLabel') primaryActionLabel: string;
  /**
   * Callback when the primary action is triggered.
   */
  @Output('sheetPrimaryAction') primaryAction = new EventEmitter();

  @HostListener('click')
  onClick() {
    this.openSheet();
  }

  /**
   * Opens the sheet.
   */
  private openSheet() {
    const sheetConfig: SheetConfig = {
      closeOnBackdropClick: this.closeOnBackdropClick,
      content: {
        closeButton: this.closeButton,
        title: this.title,
        body: this.body,
      }
    };

    if (this.primaryActionLabel || this.secondaryActionLabel) {
      sheetConfig.content.actions = [];

      if (this.primaryActionLabel) {
        sheetConfig.content.actions.push({
          label: this.primaryActionLabel,
          type: 'primary',
          tag: PRIMARY_ACTION_TAG
        });
      }

      if (this.secondaryActionLabel) {
        sheetConfig.content.actions.push({
          label: this.secondaryActionLabel,
          type: 'secondary',
          tag: SECONDARY_ACTION_TAG
        });
      }
    }

    const sheetRef = this.sheetService.open(sheetConfig);

    sheetRef.afterClosed().subscribe((result: any) => {
      switch (result) {
        case PRIMARY_ACTION_TAG:
          if (this.primaryAction.observers.length > 0) {
            this.primaryAction.emit(result);
          }
          break;
        case SECONDARY_ACTION_TAG:
          if (this.secondaryAction.observers.length > 0) {
            this.secondaryAction.emit(result);
          }
          break;
        default:
          if (this.close.observers.length > 0) {
            this.close.emit();
          }
          break;
      }
    });
  }
}
